import React from "react";
import { Row, Col, Typography, Button, Card } from "antd";
import { motion } from "framer-motion";

import infoIcon from "./assets/info.svg";
import tramIcon from "./assets/tram.svg";
import metroIcon from "./assets/metro.svg";
import trainIcon from "./assets/train.svg";
import busIcon from "./assets/bus.svg";

import googlePlayIcon from "./assets/google-play.svg";
import appStoreIcon from "./assets/app-store.svg";

const { Title, Text, Paragraph } = Typography;

const transportItems = [
    { name: "Tram", icon: tramIcon, color: "#9CCC65" },
    { name: "Metro", icon: metroIcon, color: "#29B6F6" },
    { name: "Train", icon: trainIcon, color: "#7E57C2" },
    { name: "Bus", icon: busIcon, color: "#FFC400" },
];

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0 },
};

const App: React.FC = () => {
    return (
        <div
            style={{
                minHeight: "100vh",
                background: "linear-gradient(180deg, #4D65F8, #8F00FF)",
                padding: "40px 16px",
            }}
        >
            <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                {/* HERO */}
                <motion.div
                    initial="hidden"
                    animate="show"
                    variants={fadeUp}
                    transition={{ duration: 0.6 }}
                    style={{ textAlign: "center", marginBottom: 40 }}
                >
                    <Title
                        style={{
                            color: "#FFFFFF",
                            fontSize: "clamp(28px, 5vw, 48px)",
                            marginBottom: 8,
                        }}
                    >
                        🚆 TranSupport – Smarter Public Transport for Victoria
                    </Title>

                    <Paragraph
                        style={{
                            color: "#FFFFFF",
                            fontSize: "clamp(14px, 2vw, 18px)",
                            maxWidth: 700,
                            margin: "0 auto",
                            opacity: 0.9,
                        }}
                    >
                        <strong>TranSupport</strong> is a user-friendly mobile application built for the community. Designed to simplify
                        how you navigate Victoria’s Public Transport network. Whether you're commuting to work or heading
                        out for the day, <strong>TranSupport</strong> helps you stay one step ahead.
                    </Paragraph>
                </motion.div>

                {/* TRANSPORT SECTION */}
                <Row gutter={[16, 16]} justify="center">
                    {transportItems.map((item, index) => (
                        <Col xs={12} sm={12} md={6} key={item.name}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card
                                    hoverable
                                    style={{
                                        textAlign: "center",
                                        borderRadius: 16,
                                        background: item.color,
                                        boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
                                        transition: "all 0.3s ease",
                                        cursor: "pointer",
                                    }}
                                    bodyStyle={{ padding: 20 }}

                                    onMouseEnter={(e) => {
                                        const el = e.currentTarget;
                                        el.style.background =
                                            "linear-gradient(180deg, #4D65F8, #8F00FF)";
                                        el.style.transform = "translateY(-6px) scale(1.02)";

                                        // text → white
                                        el.querySelectorAll("span").forEach((t) => {
                                            (t).style.color = "#FFFFFF";
                                        });

                                        // icon brighten
                                        el.querySelectorAll("img").forEach((img) => {
                                            (img).style.filter = "brightness(0) invert(1)";
                                        });
                                    }}

                                    onMouseLeave={(e) => {
                                        const el = e.currentTarget;
                                        el.style.background = item.color;
                                        el.style.transform = "translateY(0) scale(1)";

                                        el.querySelectorAll("span").forEach((t) => {
                                            (t).style.color = "#000000";
                                        });

                                        el.querySelectorAll("img").forEach((img) => {
                                            (img).style.filter = "none";
                                        });
                                    }}
                                >
                                    <div
                                        style={{
                                            marginBottom: 10,
                                        }}
                                    >
                                        <img
                                            src={item.icon}
                                            alt={item.name}
                                            style={{
                                                width: 68,
                                                height: 68,
                                                filter: "contrast(1.2)",
                                                transition: "transform 0.3s ease",
                                            }}
                                        />
                                    </div>
                                    <Text
                                        strong
                                        style={{
                                            font: "18px Inter, sans-serif",
                                            color: "#000000",
                                            transition: "color 0.3s ease",
                                        }}
                                    >
                                        {item.name}
                                    </Text>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                {/* FEATURES / MARKETING COPY */}
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    variants={fadeUp}
                    transition={{ duration: 0.6 }}
                    style={{
                        marginTop: 60,
                        textAlign: "center",
                    }}
                >
                    <Title
                        level={2}
                        style={{
                            color: "#FFFFFF",
                            fontSize: "clamp(18px, 5vw, 48px)",
                            marginBottom: 8,
                        }}
                    >
                        Getting around Melbourne just got easier.
                    </Title>

                    <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                        {[
                            "Real-time tracking of all transport modes",
                            "Reliable alerts for delays and disruptions",
                            "Share incidence and disruptions with the community",
                            "Save journeys, stops and routes for quick access",
                        ].map((text, i) => (
                            <Col xs={24} sm={12} key={i}>
                                <Card
                                    hoverable
                                    style={{
                                        textAlign: "center",
                                        borderRadius: 16,
                                        background: "#FFFFFF",
                                        boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
                                        transition: "all 0.3s ease",
                                        cursor: "pointer",
                                    }}

                                    bodyStyle={{
                                        padding: 10,
                                        display: "flex",
                                        flexDirection: "rows",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}

                                    onMouseEnter={(e) => {
                                        const el = e.currentTarget;
                                        el.style.background =
                                            "linear-gradient(180deg, #4D65F8, #8F00FF)";
                                        el.style.transform = "translateY(-6px) scale(1.02)";

                                        // text → white
                                        el.querySelectorAll("span").forEach((t) => {
                                            (t).style.color = "#FFFFFF";
                                        });

                                        // icon brighten
                                        el.querySelectorAll("img").forEach((img) => {
                                            (img).style.filter = "brightness(0) invert(1)";
                                        });
                                    }}

                                    onMouseLeave={(e) => {
                                        const el = e.currentTarget;
                                        el.style.background ="#FFFFFF";
                                        el.style.transform = "translateY(0) scale(1)";

                                        el.querySelectorAll("span").forEach((t) => {
                                            (t).style.color = "#000000";
                                        });

                                        el.querySelectorAll("img").forEach((img) => {
                                            (img).style.filter = "none";
                                        });
                                    }}
                                >
                                    <div style={{ display: "flex", flexDirection: "rows", alignItems: "center" }}>
                                        <img
                                            src={infoIcon}
                                            alt={"Information"}
                                            style={{
                                                width: 58,
                                                height: 58,
                                                filter: "contrast(1.2)",
                                                transition: "transform 0.3s ease",
                                            }}
                                        />
                                        <Text
                                            style={{
                                                color: "#000000",
                                                fontSize: "clamp(14px, 2vw, 20px)",
                                                maxWidth: 700,
                                                margin: "0 auto",
                                                opacity: 0.9,
                                            }}
                                        >{text}</Text>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </motion.div>

                {/* CTA */}
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    variants={fadeUp}
                    transition={{ duration: 0.6 }}
                    style={{
                        marginTop: 60,
                        textAlign: "center",
                    }}
                >
                    <Title level={3} style={{ color: "#FFFFFF" }}>
                        Get Started Today
                    </Title>

                    <Paragraph
                        style={{
                            color: "#FFFFFF",
                            fontSize: "clamp(14px, 2vw, 18px)",
                            maxWidth: 700,
                            margin: "0 auto 30px",
                            opacity: 0.9,
                        }}
                    >
                        Install <strong>TranSupport</strong> — because catching public transport shouldn’t be complicated.
                    </Paragraph>

                    <Row justify="center" gutter={[24, 24]}>
                        {/* Google Play */}
                        <Col xs={24} sm={12} md={6}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    height: 120, // 🔥 equal height for both
                                }}
                            >
                                {/* Icon */}
                                <img
                                    src={googlePlayIcon}
                                    alt="Google Play"
                                    style={{
                                        height: 50,
                                        objectFit: "contain",
                                    }}
                                />

                                {/* Button */}
                                <a href="#" target="_blank" style={{ width: "100%" }}>
                                    <Button
                                        block
                                        style={{
                                            background: "#34A853",
                                            color: "#FFFFFF",
                                            borderRadius: 10,
                                            fontWeight: "bold",
                                            height: 60,
                                            maxWidth: 140,
                                            margin: "0 auto",
                                        }}
                                    >
                                        INSTALL
                                    </Button>
                                </a>
                            </div>
                        </Col>

                        {/* App Store */}
                        <Col xs={24} sm={12} md={6}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    height: 120, // 🔥 equal height for both
                                }}
                            >
                                {/* Icon */}
                                <img
                                    src={appStoreIcon}
                                    alt="Google Play"
                                    style={{
                                        height: 50,
                                        objectFit: "contain",
                                    }}
                                />

                                {/* Button */}
                                <a href="#" target="_blank" style={{ width: "100%" }}>
                                    <Button
                                        block
                                        style={{
                                            background: "#056DFF",
                                            color: "#FFFFFF",
                                            borderRadius: 10,
                                            fontWeight: "bold",
                                            height: 60,
                                            maxWidth: 140,
                                            margin: "0 auto",
                                        }}
                                    >
                                        INSTALL
                                    </Button>
                                </a>
                            </div>
                        </Col>
                    </Row>
                </motion.div>
            </div>
        </div>
    );
};

export default App;
